<template>
    <div>
        <!-- Hero -->
        <section class="angebote-hero">
            <div class="m-container py-10">
                <div class="m-row" style="align-items: center;">
                    <div class="m-col m-col-12 m-col-md-7">
                        <MChip
                            color="secondary"
                            variant="flat"
                            size="small"
                            class="mb-3 text-none font-weight-bold"
                            prepend-icon="fire"
                        >
                            Täglich aktualisiert
                        </MChip>
                        <h1 class="titel-h1 font-weight-bold mb-3">
                            Top-Angebote & Schnäppchen
                        </h1>
                        <p class="text-body-1 text-medium-emphasis mb-0">
                            Die besten Preise aus hunderten Möbel-Shops —
                            handverlesen und täglich aktuell.
                        </p>
                    </div>
                    <div
                        class="m-col m-col-12 m-col-md-5 d-flex justify-end ga-3 flex-wrap"
                    >
                        <div class="stat-chip">
                            <MIcon
                                icon="tag-multiple"
                                :size="20"
                                color="primary"
                            />
                            <div>
                                <div class="stat-chip__value">
                                    <template v-if="stats">
                                        {{
                                            stats.total.toLocaleString("de-DE")
                                        }}+
                                    </template>
                                    <MSkeletonLoader
                                        v-else
                                        type="text"
                                        :width="50"
                                    />
                                </div>
                                <div class="stat-chip__label">Angebote</div>
                            </div>
                        </div>
                        <div class="stat-chip">
                            <MIcon
                                icon="store"
                                :size="20"
                                color="primary"
                            />
                            <div>
                                <div class="stat-chip__value">
                                    <template v-if="stats">
                                        {{
                                            stats.merchantCount.toLocaleString(
                                                "de-DE",
                                            )
                                        }}+
                                    </template>
                                    <MSkeletonLoader
                                        v-else
                                        type="text"
                                        :width="40"
                                    />
                                </div>
                                <div class="stat-chip__label">Shops</div>
                            </div>
                        </div>
                        <div class="stat-chip">
                            <MIcon
                                icon="percent"
                                :size="20"
                                color="secondary"
                            />
                            <div>
                                <div class="stat-chip__value">
                                    <template v-if="stats">
                                        bis -{{ stats.maxDiscount }}%
                                    </template>
                                    <MSkeletonLoader
                                        v-else
                                        type="text"
                                        :width="55"
                                    />
                                </div>
                                <div class="stat-chip__label">Rabatt</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <div class="m-container py-8">
            <!-- Filter Bar -->
            <div class="m-row mb-4" style="align-items: center;">
                <!-- Kategorie-Chips -->
                <div class="m-col m-col-12 d-flex flex-wrap ga-2 mb-3">
                    <MBtn
                        v-for="cat in filterCategories"
                        :key="String(cat.id)"
                        :variant="
                            activeCategoryId === cat.id ? 'flat' : 'outlined'
                        "
                        :color="
                            activeCategoryId === cat.id ? 'primary' : 'default'
                        "
                        rounded="pill"
                        size="small"
                        class="text-none"
                        @click="selectCategory(cat.id)"
                    >
                        {{ cat.name }}
                    </MBtn>
                </div>

                <!-- Mindest-Rabatt + Sortierung -->
                <div class="m-col m-col-12 d-flex flex-wrap align-center ga-2">
                    <span class="text-caption text-medium-emphasis mr-1"
                        >Mindest-Rabatt:</span
                    >
                    <MBtn
                        v-for="opt in minDiscountOptions"
                        :key="opt.value"
                        :variant="
                            minDiscount === opt.value ? 'flat' : 'outlined'
                        "
                        :color="
                            minDiscount === opt.value ? 'secondary' : 'default'
                        "
                        rounded="pill"
                        size="small"
                        class="text-none"
                        @click="setMinDiscount(opt.value)"
                    >
                        {{ opt.label }}
                    </MBtn>

                    <span class="m-spacer" />

                    <MSelect
                        v-model="sortBy"
                        :items="sortOptions"
                        item-title="label"
                        item-value="value"
                        density="compact"
                        variant="outlined"
                        rounded="lg"
                        hide-details
                        style="max-width: 180px"
                    />
                </div>
            </div>

            <!-- Ergebnis-Zähler -->
            <div
                v-if="!initialLoading"
                class="text-caption text-medium-emphasis mb-4"
            >
                {{ total.toLocaleString("de-DE") }} Angebote gefunden
            </div>

            <!-- Skeleton Initial Load -->
            <div class="m-row" v-if="initialLoading">
                <div class="m-col m-col-6 m-col-sm-4 m-col-md-3" v-for="n in 12" :key="n">
                    <MSkeletonLoader type="image, article" rounded="lg" />
                </div>
            </div>

            <template v-else-if="products.length">
                <div class="m-row">
                    <div
                        v-for="product in products"
                        :key="product.id"
                        class="m-col m-col-6 m-col-sm-4 m-col-md-3"
                    >
                        <ProductCard :product="product" />
                    </div>
                </div>

                <InfiniteScroll
                    :loading="loadingMore"
                    :has-more="hasMore"
                    @load="loadMore"
                >
                    WEITERE PRODUKTE LADEN
                    <span style="opacity: 0.8; font-weight: 400;">
                        ({{ products.length }} VON
                        {{ total.toLocaleString("de-DE") }})
                    </span>
                </InfiniteScroll>
            </template>

            <div v-else class="text-center py-16">
                <MIcon
                    icon="tag-off"
                    :size="64"
                    color="grey-lighten-2"
                    class="mb-4"
                />
                <p class="text-h6 text-medium-emphasis">
                    Keine Angebote gefunden
                </p>
                <MBtn
                    variant="outlined"
                    color="primary"
                    rounded="pill"
                    class="mt-4 text-none"
                    @click="resetFilter"
                >
                    Filter zurücksetzen
                </MBtn>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
const api = useApi();

useSeoMeta({
    title: "Top-Angebote & Schnäppchen",
    description: "Die besten Möbel-Angebote und Schnäppchen aus hunderten Online-Shops. Täglich aktualisiert, kostenlos verglichen.",
    ogTitle: "Möbel-Angebote — bis zu 70% Rabatt | Moebix.de",
    ogDescription: "Täglich aktualisierte Schnäppchen aus hunderten Online-Möbelshops. Kostenlos vergleichen und sparen.",
    ogImage: "/moebix_og_sticker.jpg",
    ogType: "website",
    ogUrl: "https://www.moebix.de/angebote",
});

useHead({
    link: [{ rel: "canonical", href: "https://www.moebix.de/angebote" }],
    script: [{
        type: "application/ld+json",
        innerHTML: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "Möbel-Angebote & Schnäppchen",
            description: "Täglich aktualisierte Möbel-Angebote aus hunderten Online-Shops.",
            url: "https://www.moebix.de/angebote",
        }),
    }],
});

const PER_PAGE = 24;

const page = ref(1);
const activeCategoryId = ref<string | undefined>(undefined);
const sortBy = ref("discount");
const minDiscount = ref(0);
const products = ref<any[]>([]);
const total = ref(0);
const initialLoading = ref(true);
const loadingMore = ref(false);
const hasMore = ref(true);

const sortOptions = [
    { label: "Größter Rabatt", value: "discount" },
    { label: "Niedrigster Preis", value: "price_asc" },
    { label: "Höchster Preis", value: "price_desc" },
    { label: "Neueste", value: "newest" },
];

const minDiscountOptions = [
    { label: "Alle", value: 0 },
    { label: "≥10%", value: 10 },
    { label: "≥20%", value: 20 },
    { label: "≥30%", value: 30 },
    { label: "≥50%", value: 50 },
];

const navigationStore = useNavigationStore();

onMounted(() => {
    navigationStore.fetchNavigation();
});

const filterCategories = computed(() => [
    { id: undefined, name: "Alle" },
    ...navigationStore.items.slice(0, 8),
]);

// Hero-Stats dynamisch laden
const { data: statsData } = useAsyncData("discount-stats",
    () => api<any>("/api/products/discount-stats"),
    { lazy: true },
);
const stats = computed(() => statsData.value);

const fetchProducts = async (reset = false) => {
    if (reset) {
        initialLoading.value = true;
        page.value = 1;
        hasMore.value = true;
    } else {
        if (!hasMore.value || loadingMore.value) return;
        loadingMore.value = true;
    }

    try {
        const data = await api<{ products: any[]; total: number }>(
            "/api/products",
            {
                query: {
                    categoryId: activeCategoryId.value,
                    page: page.value,
                    perPage: PER_PAGE,
                    orderBy: sortBy.value,
                    hasDiscount: true,
                    minDiscount:
                        minDiscount.value > 0 ? minDiscount.value : undefined,
                },
            },
        );

        total.value = data.total;

        if (reset) {
            products.value = data.products;
        } else {
            products.value.push(...data.products);
        }

        if (data.products.length < PER_PAGE) {
            hasMore.value = false;
        }
    } finally {
        initialLoading.value = false;
        loadingMore.value = false;
    }
};

// Initial load
await fetchProducts(true);

const selectCategory = (id: string | undefined) => {
    activeCategoryId.value = id;
    fetchProducts(true);
};

const setMinDiscount = (val: number) => {
    minDiscount.value = val;
    fetchProducts(true);
};

const resetFilter = () => {
    activeCategoryId.value = undefined;
    minDiscount.value = 0;
    sortBy.value = "discount";
    fetchProducts(true);
};

watch(sortBy, () => fetchProducts(true));

const loadMore = () => {
    page.value++;
    fetchProducts(false);
};
</script>

<style scoped>
.angebote-hero {
    background: linear-gradient(135deg, #f6f6f6 0%, #e8f4f5 100%);
    border-bottom: 1px solid rgba(60, 143, 147, 0.15);
}

.stat-chip {
    display: flex;
    align-items: center;
    gap: 10px;
    background: white;
    border: 1px solid rgba(60, 143, 147, 0.2);
    border-radius: var(--m-radius);
    padding: 10px 16px;
}

.stat-chip__value {
    font-family: "Justtellmewhat", sans-serif;
    font-size: 1.1rem;
    font-weight: 700;
    font-style: italic;
    color: #3c8f93;
    line-height: 1;
}

.stat-chip__label {
    font-size: 0.75rem;
    color: rgba(0, 0, 0, 0.5);
    line-height: 1.2;
}
</style>
